import React, {forwardRef} from "react"
import cx from "classnames"
import Icon from "./icons"

// Label, Inputs, Textarea

export const Label = ({className, error, children, ...props}) => (
  <div className={cx(className, "font-vs-regular text-sm ml-[1.125rem] mb-2 leading-none text-left", error && "text-danger-1")} {...props}>
    {children}
  </div>
)

export const Input = forwardRef(({className, type = "text", label = "Label", onChange, error, ...props}, ref) => {
  return (
    <label className={cx("block", className)}>
      <Label>{label}</Label>
      <input className={cx("input", error && "border-danger-1")} onChange={(e) => onChange(e.target.value)} ref={ref} {...props} />
    </label>
  )
})

export const Textarea = ({className, label, ...props}) => {
  return (
    <label className="block">
      <Label>{label}</Label>
      <textarea className={cx("textarea", className)} {...props} />
    </label>
  )
}

export const Radio = ({className, label, ...props}) => {
  return (
    <label className="relative flex gap-x-2 items-center">
      <input type="radio" className="appearance-none absolute inset-0 cursor-pointer peer" {...props} />
      <Icon name="radio-circle-icon-selected" className="hidden peer-checked:block" />
      <Icon name="large-circle" className="peer-checked:hidden" />
      <span className="text-sm md:text-md">{label}</span>
    </label>
  )
}

// Support and error messages

export const Message = ({className, children, ...props}) => (
  <div className={cx(className, "text-xs text-neutral-500 mt-2 ml-[1.125rem]")} {...props}>
    {children}
  </div>
)

export const ErrorMessage = ({className, children, ...props}) => (
  <Message className={cx(className, "flex gap-x-2 text-neutral-2")} {...props}>
    <Icon name="white-circled-exclamation-mark" size="text-md" className="text-danger-1 mt-[1px] shrink-0" />
    {children}
  </Message>
)

export const InputGroup = ({className, children, ...props}) => (
  <div className={cx(className, "mb-6")} {...props}>
    {children}
  </div>
)

export const Select = ({className, selectClassName, label = "Label", hideLabel, children, ...props}) => (
  <label className={cx(className, "block")}>
    <Label className={hideLabel && "sr-only"}>{label}</Label>
    <div className="relative">
      <select
        className={cx("relative z-[1] font-main font-vs-regular appearance-none bg-transparent box-border leading-base w-full border-2 border-neutral-18 rounded-xl py-3 px-4 hover:border-neutral-3 focus-within:outline-neutral-3 placeholder:text-neutral-3 cursor-pointer", selectClassName)}
        {...props}
      >
        {children}
      </select>
      <svg className="absolute right-4 translate-y-center" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M6.55004 10.8846C5.62504 9.93457 6.27504 8.30957 7.62504 8.30957H16.375C17.725 8.30957 18.375 9.93457 17.45 10.8846L13.05 15.2596C12.475 15.8346 11.525 15.8346 10.95 15.2596L6.55004 10.8846Z"
          fill="black"
        />
      </svg>
    </div>
  </label>
)

export const SelectAccessible = ({className, selectClassName, label = "Label", hideLabel, children, id, ...props}) => (
  <div className={className}>
    <label htmlFor={id} className={cx("font-vs-regular text-sm ml-[1.125rem] mb-2 leading-none text-left block", hideLabel && "sr-only")}>
      {label}
    </label>
    <div className="relative">
      <select
        id={id}
        className={cx("relative z-[1] font-main font-vs-regular appearance-none bg-transparent box-border leading-base w-full border-2 border-neutral-18 rounded-xl py-3 px-4 hover:border-neutral-3 focus-within:outline-neutral-3 placeholder:text-neutral-3 cursor-pointer", selectClassName)}
        {...props}
      >
        {children}
      </select>
      <svg className="absolute right-4 translate-y-center" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M6.55004 10.8846C5.62504 9.93457 6.27504 8.30957 7.62504 8.30957H16.375C17.725 8.30957 18.375 9.93457 17.45 10.8846L13.05 15.2596C12.475 15.8346 11.525 15.8346 10.95 15.2596L6.55004 10.8846Z"
          fill="black"
        />
      </svg>
    </div>
  </div>
)

export const SearchInput = ({className, label = "Search", hideLabel, placeholder = "Search...", ...props}) => {
  return (
    <label className={cx("block", className)}>
      <Label className={cx(hideLabel && "sr-only")}>{label}</Label>
      <div className="relative h-[3.25rem] md:h-14">
        <Icon name="left-pointing-magnifying-glass" className="absolute left-4 translate-y-center z-[1]" />
        <input className="input pl-12 outline-offset-0 bg-transparent absolute inset-0 w-full h-full z-[2] md:text-md" type="search" placeholder={placeholder} {...props} />
      </div>
    </label>
  )
}
